import { Component, OnInit, ViewChild } from '@angular/core';
import { ValueStreamConfiguration } from '@app/shared/models/value-stream-configuration.model';
import { ToastsManager } from "@app/shared/util/toastr.service";
import { TranslateParser, TranslateService } from '@ngx-translate/core';
import { TableConfig } from 'app/shared/util/table-config.utils';
import { TableBasedComponent } from "app/shared/interface/TableBasedComponent";
import { Log } from "ng2-logger";
import { PermissionService } from "@app/authentication/services/permission.service";
import { AppUtils } from "@app/shared/util/app.utils";
import { ValueStreamCreateModalComponent } from "./components/value-stream-create-modal/value-stream-create-modal.component";
import { ValueStreamService } from '@app/core/services/value-stream.service';

@Component({
    selector: 'app-value-streams',
    templateUrl: './value-streams.component.html',
    styleUrls: ['./value-streams.component.scss']
})
export class ValueStreamManagementComponent implements OnInit, TableBasedComponent<ValueStreamConfiguration> {
    _logger = Log.create(ValueStreamManagementComponent.name);

    @ViewChild(ValueStreamCreateModalComponent, { static: true })
    valueStreamFormModal: ValueStreamCreateModalComponent;

    valueStreams: ValueStreamConfiguration[] = [];
    selectedValueStream: ValueStreamConfiguration = new ValueStreamConfiguration();

    tableConfig = new TableConfig();
    isLoading = false;

    constructor(private valueStreamService: ValueStreamService,
                private toastr: ToastsManager,
                private translationService: TranslateService,
                private permissionService: PermissionService,
                private translateParser: TranslateParser) {}

    ngOnInit() {
        this.getAll();
    }

    openModal() {
        this.selectedValueStream = new ValueStreamConfiguration();
        this.valueStreamFormModal.open();
    }

    onDuplicateRow(vs: ValueStreamConfiguration) {
        vs.valueStreamName = vs.valueStreamName + AppUtils.COPY_STRING;
        this.valueStreamFormModal.open(vs, true);
    }

    getAll() {
        this.isLoading = true;

        this.valueStreamService.getAll(this.tableConfig).subscribe(
            (data: any) => {
                this.valueStreams = data.items;
                this.tableConfig.pager.count = data.total;
                this.isLoading = false;
            },
            error => {
                this.isLoading = false;
            }
        );
    }

    create(vs: ValueStreamConfiguration) {
        this.isLoading = true;

        this.valueStreamService.create(vs).subscribe(
            data => {
                this.getAll();
                this.toastr.success(this.buildCreateMessage(data.valueStreamName));
                this.valueStreamFormModal.close();
                this.isLoading = false;
            },
            error => this.isLoading = false
        );
    }

    update(vsUpdate) {
        this.isLoading = true;
        this.valueStreamService.update(vsUpdate.newValueStream).subscribe(
            data => {
                this.getAll();
                this.toastr.success(this.buildUpdateMessage(vsUpdate.previousName));
                this.valueStreamFormModal.close();
                this.isLoading = false;
            },
            error => this.isLoading = false
        );
    }

    remove(vs: ValueStreamConfiguration) {
        this.isLoading = true;

        this.valueStreamService.delete(vs).subscribe(
            () => {
                this.getAll();
                this.toastr.warning(this.buildDeleteMessage(vs.valueStreamName));
                this.valueStreamFormModal.close();
                this.isLoading = false;
            },
            error => this.isLoading = false
        );
    }

    onSortChange(event) {
        this.getAll();
    }

    onPageChange(event) {
        this.getAll();
    }

    onFilter(event) {
        this.getAll();
    }

    onTableRowClick(vs) {
        this.selectedValueStream = vs;
        this.valueStreamFormModal.open(vs);
    }

    buildCreateMessage(valueStreamName ) {
        const msg = this.translationService.instant('admin.valueStreams.toasts.created');
        return this.translateParser.interpolate(msg, { valueStreamName  });
    }

    buildUpdateMessage(valueStreamName ) {
        const msg = this.translationService.instant('admin.valueStreams.toasts.updated');
        return this.translateParser.interpolate(msg, { valueStreamName  });
    }

    buildDeleteMessage(valueStreamName ) {
        const msg = this.translationService.instant('admin.valueStreams.toasts.deleted');
        return this.translateParser.interpolate(msg, { valueStreamName  });
    }

    shouldRenderModifiers() {
        return this.permissionService.hasHasPermissionForFeature('MANAGE_VALUE_STREAMS'); 
    }
}
